import { getApi } from '../utils/api';
import { API_ENDPOINTS } from '../utils/endpoints';
import { log } from '../utils/logger';

const AUTH_TOKEN_KEY = process.env.REACT_APP_AUTH_TOKEN_KEY;

export const authService = {
  /**
   * Verifies a magic link token
   * @param {string} token - Magic link token from URL
   * @returns {Promise<{ success: boolean, message?: string }>}
   */
  verifyMagicLink: async (token) => {
    try {
      // Call the backend verify endpoint with the token
      // Ensure token is properly encoded for URL
      const encodedToken = encodeURIComponent(token);
      log('Verifying token:', { original: token, encoded: encodedToken });
      
      const response = await getApi().get(`${API_ENDPOINTS.AUTH.VERIFY}/token/${encodedToken}`, {
        validateStatus: function (status) {
          return status < 500; // Accept any status code less than 500
        },
        // Ensure cookies are included
        withCredentials: true
      });

      // For 400 responses, return error
      if (response.status === 400) {
        return {
          success: false,
          message: response.data.detail || 'Failed to verify magic link'
        };
      }
      
      // For successful responses
      if (response.data) {
        // Save the JWT token if present
        if (response.data.token && response.data.token.split('.').length === 3) {
          localStorage.setItem(AUTH_TOKEN_KEY, response.data.token);
          getApi().instance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
        }
        // Optionally save user info
        if (response.data.user) {
          localStorage.setItem('userInfo', JSON.stringify(response.data.user));
        }
        return {
          success: true,
          ...response.data
        };
      }
      
      // Fallback error
      return {
        success: false,
        message: 'Invalid response from server'
      };
    } catch (error) {
      log('Verification error:', error.response || error);
      return {
        success: false,
        message: error.response?.data?.detail || error.message || 'Failed to verify magic link'
      };
    }
  },

  /**
   * Initiates the login process by requesting a magic link
   * @param {string} email - User's email address
   * @returns {Promise<{ success: boolean, message: string }>}
   */
  initiateLogin: async (email = null) => {
    try {
      // Always use POST, with or without email
      const response = await getApi().post(API_ENDPOINTS.AUTH.LOGIN, email ? { email } : {}, {
        withCredentials: true // Include cookies
      });
      
      const { status, user, stores, message } = response.data;
      
      if (status === 'authenticated' && user) {
        // Optionally save user info
        localStorage.setItem('userInfo', JSON.stringify(user));
        return {
          success: true,
          user,
          stores,
          isAuthenticated: true
        };
      }
      
      return {
        success: true,
        message: message || 'Magic link sent',
        isAuthenticated: false
      };
         } catch (error) {
       if (error.response?.status === 401) {
         return {
           success: false,
           isAuthenticated: false
         };
       }
       if (error.response?.status === 400) {
         // Check if it's an email not found error
         const errorDetail = error.response?.data?.detail;
         if (errorDetail && errorDetail.includes('No active user found with this email')) {
           return {
             success: false,
             message: 'Email not registered. Please reach out to service@tdpbakery.com to register the email',
             isAuthenticated: false
           };
         }
         // Otherwise treat as maintenance error
         return {
           success: false,
           message: 'Site under maintenance. Please try again later.',
           isAuthenticated: false
         };
       }
       log('Error in login:', error);
       throw error;
     }
  },

  /**
   * Logs out the current user
   * @returns {Promise<{ success: boolean }>}
   */
  logout: async () => {
    try {
      await getApi().post(API_ENDPOINTS.AUTH.LOGOUT);
      // Clear auth state from local storage
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem('userInfo');
      return {
        success: true,
        message: 'Logged out successfully'
      };
    } catch (error) {
      log('Logout error:', error);
      return {
        success: false,
        message: error.response?.data?.detail || error.message || 'Failed to logout'
      };
    }
  }
};
